<template>
  <div class="error-container">
    <!-- Animated Background Glows -->
    <div class="glow glow-1"></div>
    <div class="glow glow-2"></div>
    
    <!-- Subtle Grid Overlay -->
    <div class="grid-overlay"></div>

    <div class="content-wrapper">
      <!-- The large 404 Text with Gradient -->
      <h1 class="error-code">404</h1>
      
      <!-- Divider Line -->
      <div class="divider"></div>

      <!-- Typography -->
      <p class="error-subtitle">دور بزن!! اشتباه اومدی</p>
      <p class="error-description">
        صفحه مورد نظر یافت نشد. ممکن است حذف شده باشد یا آدرس آن تغییر کرده باشد.
      </p>

      <!-- Action Buttons -->
      <div class="button-group">
        <button @click="handleError" class="btn btn-primary">
          <span>برگشت به صفحه اصلی</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
        
        <button @click="goBack" class="btn btn-ghost">
          <span>بازگشت</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()

const route = useRoute()

onMounted(async () => {
  // 1. Resolve broken URL
  const brokenTarget = route.fullPath || (props.error as any)?.url || ''

  // 2. Resolve source/origin page
  const sourcePage = (window.history.state && window.history.state.back)
    ? window.history.state.back
    : (document.referrer || 'Direct Navigation')

  const payload = {
    brokenUrl: decodeURIComponent(brokenTarget),
    clickedFrom: sourcePage,
    statusCode: props.error?.statusCode || 404,
    message: props.error?.message,
    userAgent: navigator.userAgent
  }

  // 3. Send to Nitro server so it logs in Docker STDOUT
  // try {
  //   await $fetch('/api/log-broken-link', {
  //     method: 'POST',
  //     body: payload
  //   })
  // } catch (err) {
  //   // Fail silently so it doesn't interrupt the user experience
  // }
})

const handleError = () => clearError({ redirect: '/' })

const goBack = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    window.history.back()
  } else {
    clearError({ redirect: '/' })
  }
}
</script>

<style lang="scss" scoped>
@use "sass:color";
/* =========================================
   LAYOUT & CONTAINER
   ========================================= */
.error-container {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $green;
  overflow: hidden;
  color:$green-light;
}

/* =========================================
   BACKGROUND EFFECTS
   ========================================= */
.glow {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.4;
  animation: float 8s ease-in-out infinite;
}

.glow-1 {
  background: var(--accent-indigo);
  top: -200px;
  left: -100px;
  animation-delay: 0s;
}

.glow-2 {
  background: var(--accent-purple);
  bottom: -200px;
  right: -100px;
  animation-delay: -4s;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
  -webkit-mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
}

/* =========================================
   CONTENT & TYPOGRAPHY
   ========================================= */
.content-wrapper {
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 2rem;
  max-width: 600px;
}

.error-code {
  font-size: clamp(8rem, 20vw, 14rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.05em;
  margin: 0;

  filter: drop-shadow(0 0 30px rgba(129, 140, 248, 0.3));
  animation: pulse-glow 4s ease-in-out infinite alternate;
}

.divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, var(--accent-indigo), var(--accent-purple));
  margin: 1.5rem auto;
  border-radius: 2px;
}

.error-subtitle {
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: var(--accent-indigo);
  margin: 0 0 1rem 0;
}

.error-description {
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 2.5rem 0;
}

/* =========================================
   BUTTONS
   ========================================= */
.button-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
}

.btn-primary {
  background-color: var(--text-primary);
  color: var(--bg-primary);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.25);
  background-color: #ffffff;
}

.btn-ghost {
  background-color: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(4px);
}

.btn-ghost:hover {
  color: var(--text-primary);
  border-color: rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
}

/* =========================================
   ANIMATIONS
   ========================================= */
@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

@keyframes pulse-glow {
  0% { filter: drop-shadow(0 0 20px rgba(129, 140, 248, 0.2)); }
  100% { filter: drop-shadow(0 0 40px rgba(167, 139, 250, 0.5)); }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .button-group {
    flex-direction: column;
    align-items: center;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>